import { AbstractSidebar } from "../Sidebar/Abstract";
import cartManager from "./CartManager";
import { Choice } from "./Flow/Choice";
import { ICart, ICartItem, ICartItemProperties } from "../../Interfaces/Cart";
import {
    DATA_ATTRIBUTES,
    GUARANTEE_TYPES,
    INSURANCE_TYPE_NOT_TESTRIDE,
    PRODUCT_SERVICES_KEY,
    PRODUCT_TYPE_EBIKE,
    PRODUCT_TYPE_TESTRIDE,
    RENTAL_TYPES,
    RENTAL_TYPES_EBIKE,
    SELECTORS as CART_SELECTORS,
    SERVICE_BELONGS_TO_KEY,
    SERVICE_PROPERTY_MAP
} from "./Constants";
import { formatMoney } from "../../Utils/Money";
import LoaderOverlay from "../LoaderOverlay";
import { additionalCartItemProperties, calculateTax, regTax } from "./Helper/CartItemProperties";
import { EVENTS } from "../Product/Quantity/Constants";
import { Quantity } from "../../Components/Form/Quantity";

/**
 * Class describing Cart Items for a "Limbächer Service"
 * A service is f.ex. of type guarantee which type is usually initially chosen
 * during the process of the {@link ./FlowController}.
 * Allows representation and editing in the cart.
 *
 * This class doesn't give you many (or at least any) APIs, its purpose is to hide
 * the logic happening on a cart item.
 */
export class CartItem {
    public static readonly DATA_ATTRIBUTES = DATA_ATTRIBUTES;
    private static readonly CONFIGURE_ACCESSORIES = "configure-accessories";

    public static readonly SERVICE_TEMPLATE_MAPPING: { [key: string]: string } = {
        _guarantee: "configure-guarantee",
        _registration: "configure-registration",
        "_versicherung-vollkasko": "configure-insurance",
        _helm: CartItem.CONFIGURE_ACCESSORIES,
        _leihhelm: CartItem.CONFIGURE_ACCESSORIES,
        _leihschloss: CartItem.CONFIGURE_ACCESSORIES,
        _handschuhe: CartItem.CONFIGURE_ACCESSORIES,
        "_jacke-nierengurt": CartItem.CONFIGURE_ACCESSORIES
    };

    public static readonly SERVICE_PROPERTY_MAP = SERVICE_PROPERTY_MAP;

    private readonly cartItemElement: HTMLElement;
    private readonly cartItemKey: string;
    private cartItems: ICartItem[];
    private activeService: string;
    private saveServiceHandler: () => void;
    private readonly cartItem: ICartItem;

    /**
     * Init handlers for a cart item
     * @param cartItem element to extract data from
     * @param cartItems
     */
    constructor(cartItem: HTMLElement, cartItems: ICartItem[]) {
        this.cartItemElement = cartItem;
        this.cartItems = cartItems;
        this.cartItemKey = this.extractCartItemKey();
        this.cartItem = this.cartItems.find((i) => i.key === this.cartItemKey);
        this.addEventListeners();
        this.initQuantityInput();
    }

    /**
     * Init click event listeners for the edit buttons
     */
    private addEventListeners(): void {
        this.saveServiceHandler = this.handleSaveService.bind(this);
        this.removeFromCartButton.addEventListener("click", this.removeFromCart.bind(this));
        this.addEditEventListeners();
    }

    /**
     * Add event listener to every edit button (pencil icon next to the option) that opens the options
     * for that specific service again
     */
    private addEditEventListeners(): void {
        this.editButtons.forEach((editButton: HTMLElement) => {
            editButton.addEventListener("click", () => {
                const serviceVariant = editButton.closest<HTMLElement>(`[${CartItem.DATA_ATTRIBUTES.SERVICE_HANDLE}]`)
                    .dataset.serviceHandle;
                if (this.activeService && serviceVariant == this.activeService) {
                    this.destroyServiceForm();
                    this.activeService = null;
                } else {
                    this.initServiceConfigurator(this.cartItemKey, serviceVariant);
                }
            });
        });
    }

    /**
     * Apply event listeners that listen to clicks on the options for a service (after clicking its edit button to
     * open them).
     *
     * See {@link Choice#addListeners()}, but there's no simple sense in implementing the same class hierarchy in
     * this case or adopting that model here.
     * @param option Sidebar element in this cart item
     */
    private static addChoiceEventListeners(option: HTMLElement): void {
        const allowMultiselect = option.classList.contains(CartItem.CONFIGURE_ACCESSORIES);
        const choices = option.querySelectorAll(`[${CartItem.DATA_ATTRIBUTES.CHOICE}]`) as NodeListOf<HTMLElement>;
        Choice.addChoiceEventListeners(choices, allowMultiselect, option);
    }

    /**
     * Extract cart item key from the attribute of the current cart item element
     * @private
     */
    private extractCartItemKey(): string {
        return this.cartItemElement.dataset.itemKey;
    }

    /**
     * Render template of the flow configuration options for a service
     *
     * @param cartItemKey shopify's key for the cart item
     * @param serviceHandle serviceHandle id for the service that is being edited
     */
    private initServiceConfigurator(cartItemKey: string, serviceHandle: string): void {
        if (this.activeService) this.destroyServiceForm();
        this.activeService = serviceHandle;
        const template: HTMLTemplateElement = document
            .querySelector(`#${CartItem.SERVICE_TEMPLATE_MAPPING[serviceHandle]}`)
            .cloneNode(true) as HTMLTemplateElement;
        const content: HTMLElement = template.content
            .querySelector(AbstractSidebar.SELECTORS.ITEM)
            .cloneNode(true) as HTMLDivElement;
        // Remove "back"-button since it only makes sense in the actual cart flow
        content.querySelector<HTMLElement>(".button-box > .invert")?.remove();
        const buttonBox = content.querySelector<HTMLElement>(".button-box");
        const saveButton = buttonBox.querySelector<HTMLElement>(".primary");
        saveButton?.addEventListener("click", this.saveServiceHandler, { once: true });
        saveButton.innerHTML = buttonBox.dataset.i18nSubmit;
        CartItem.getServiceElementsByHandle(cartItemKey, serviceHandle).appendChild(content);
        this.initChoices(content, serviceHandle);
    }

    /**
     * Event handler for the click on a save button after choosing any service option
     */
    private async handleSaveService(event: Event): Promise<void> {
        event.preventDefault();
        new LoaderOverlay().render();
        (<HTMLElement>event.currentTarget).setAttribute("disabled", "disabled");
        const serviceHandle = (<HTMLElement>event.currentTarget).closest<HTMLElement>(
            `[${CartItem.DATA_ATTRIBUTES.SERVICE_HANDLE}]`
        )?.dataset.serviceHandle;
        let services = (this.cartItem.properties?.[PRODUCT_SERVICES_KEY] as string[]) || [];
        const updatedChoice = this.openServiceElement.dataset.choice;
        const possibleVariantsForServiceElement = this.possibleVariants;
        // Deselected service -> remove from _lim_services array
        services = services.filter((service) => !possibleVariantsForServiceElement.includes(service));
        // Merge current services with new choice
        if (updatedChoice) {
            services = [...services, ...JSON.parse(this.openServiceElement.dataset.choice)];
        }

        const serviceQuantities = this.getServiceQuantities(services, serviceHandle);
        await cartManager.updateServiceProperties(
            String(this.cartItemKey),
            services,
            serviceQuantities,
            this.retrieveAdditionalProperties()
        );
        window.location.reload();
    }

    /**
     * Remove form to update service from DOM
     */
    private destroyServiceForm(): void {
        const openForm: HTMLElement = this.openServiceElement;
        openForm?.querySelector(".button-box > .primary")?.removeEventListener("click", this.saveServiceHandler);
        openForm?.remove();
    }

    /**
     * Event handler for remove-from-cart buttons containing logic for the cart service removal.
     */
    private async removeFromCart(): Promise<void> {
        new LoaderOverlay().render();
        const currentCart = await cartManager.load();
        const itemToRemove = currentCart.items.find((item) => item.key === this.cartItemKey);
        let quantityToRemove = 1;
        if (
            RENTAL_TYPES.concat(RENTAL_TYPES_EBIKE).includes(itemToRemove.product_type) ||
            itemToRemove.product_type === PRODUCT_TYPE_EBIKE
        ) {
            quantityToRemove = itemToRemove.quantity;
        }
        const updates = { [this.cartItemKey]: itemToRemove.quantity - quantityToRemove };
        // Decrement quantity in case the same service product is used by different other products
        for (const serviceItem of this.serviceItemsToRemove(itemToRemove, currentCart)) {
            quantityToRemove = Number(
                this.cartItem.properties?.[SERVICE_PROPERTY_MAP[serviceItem.handle]?.service_quantity] ||
                    Math.min(quantityToRemove, serviceItem.quantity)
            );
            if (quantityToRemove > 0) {
                updates[serviceItem.key] = serviceItem.quantity - quantityToRemove;
            }
        }
        await cartManager.updateQuantities(updates);
        // The default shopify way also contains a page reload, this simplifies the document update here too
        window.location.reload();
    }

    /**
     * Get the cart items that are a service products and belong to the cart item to remove.
     *
     * @param itemToRemove cart item that should be removed together with its service products
     * @param currentCart
     * @private
     */
    private serviceItemsToRemove(itemToRemove: ICartItem, currentCart: ICart): ICartItem[] {
        const servicesToRemove =
            itemToRemove.properties && itemToRemove.properties[PRODUCT_SERVICES_KEY]
                ? (itemToRemove.properties[PRODUCT_SERVICES_KEY] as unknown as string[])
                : [];
        return servicesToRemove.length
            ? currentCart.items.filter(
                  (item) =>
                      item.properties &&
                      item.properties[SERVICE_BELONGS_TO_KEY] &&
                      item.properties[SERVICE_BELONGS_TO_KEY] === itemToRemove.id &&
                      servicesToRemove.includes(String(item.id))
              )
            : [];
    }

    /**
     * Preselect choices that were already selected before in cart flow
     *
     * @param choiceElement element containing the service choices
     * @private
     */
    private applyActiveClasses(choiceElement: HTMLElement) {
        if (!this.cartItem.properties) return;
        const services = this.cartItem.properties[PRODUCT_SERVICES_KEY] as string[];
        if (services && services.includes(choiceElement.dataset.choice)) {
            choiceElement.classList.add(Choice.ACTIVE_CLASS);
        }
    }

    /**
     * add active choices to config form elements dataset
     *
     * @param configForm
     * @private
     */
    private applyServiceChoices(configForm: HTMLElement) {
        if (!this.cartItem.properties) return;
        const choices = [
            ...configForm.querySelectorAll<HTMLElement>(`[${CartItem.DATA_ATTRIBUTES.CHOICE}].${Choice.ACTIVE_CLASS}`)
        ]
            .filter((element: HTMLElement) => element.dataset.choice)
            .map((choice) => choice.dataset.choice);
        configForm.dataset.choice = JSON.stringify(choices);
    }

    /**
     * initialize optional quantity selector
     * @param choiceElement
     * @param serviceHandle
     * @private
     */
    private initServiceQuantitySelector(choiceElement: HTMLElement, serviceHandle: string) {
        const quantitySelector = choiceElement.querySelector<Quantity>(CART_SELECTORS.QUANTITY_SELECT);
        if (quantitySelector) {
            if (this.cartItem.quantity === 1) {
                quantitySelector.remove();
            } else {
                const serviceItem = this.getServiceItemByHandle(serviceHandle);
                quantitySelector.max = String(this.cartItem.quantity);
                quantitySelector.value = String(serviceItem?.quantity || this.cartItem.quantity);
            }
        }
    }

    /**
     * Some services are only bookable for specific bikes. This filtering can not be done using
     * liquid at this point.
     *
     * @param choiceElement
     * @private
     */
    private removeForbiddenChoices(choiceElement: HTMLElement) {
        const hideRentalClassOption =
            choiceElement.dataset.rentalClass &&
            choiceElement.dataset.rentalClass !== this.cartItemElement.dataset.rentalClass;
        const productType = this.cartItemElement.dataset.productType;
        let allowedGuarantee: string;
        if (productType === PRODUCT_TYPE_EBIKE) {
            const price = parseInt(this.cartItemElement.dataset.price);
            allowedGuarantee =
                price > 4000
                    ? GUARANTEE_TYPES.EBIKE.GUARANTEE_GT_4000
                    : price < 4000
                    ? GUARANTEE_TYPES.EBIKE.GUARANTEE_LT_4000
                    : "";
        } else {
            const ccm = parseInt(this.cartItemElement.dataset.ccm);
            allowedGuarantee =
                ccm > 1000
                    ? GUARANTEE_TYPES.MOTORBIKE.GUARANTEE_1000
                    : ccm >= 701 && ccm <= 1000
                    ? GUARANTEE_TYPES.MOTORBIKE.GUARANTEE_900
                    : ccm >= 126 && ccm <= 700
                    ? GUARANTEE_TYPES.MOTORBIKE.GUARANTEE_126
                    : ccm <= 125
                    ? GUARANTEE_TYPES.MOTORBIKE.GUARANTEE_125
                    : "";
        }

        const hideTestrideOption =
            productType === PRODUCT_TYPE_TESTRIDE
                ? choiceElement.dataset.testrideOption !== undefined &&
                  choiceElement.dataset.testrideOption !== PRODUCT_TYPE_TESTRIDE
                : choiceElement.dataset.testrideOption !== undefined &&
                  choiceElement.dataset.testrideOption !== INSURANCE_TYPE_NOT_TESTRIDE;
        const hideGuaranteeOption =
            productType === PRODUCT_TYPE_EBIKE
                ? choiceElement.dataset.option && choiceElement.dataset.option !== allowedGuarantee
                : choiceElement.dataset.ccm && choiceElement.dataset.ccm !== allowedGuarantee;
        if ((hideRentalClassOption || hideGuaranteeOption || hideTestrideOption) && choiceElement.dataset.choice)
            choiceElement.remove();
    }

    /**
     * Get all possible variant IDs for the open service element
     *
     * @return {string[]} Array of choices represented by variant IDs
     * @private
     */
    private get possibleVariants(): string[] {
        return Array.from(this.openServiceElement.parentNode.querySelectorAll("div"))
            .filter((element: HTMLElement) => element.dataset.choice)
            .map((element: HTMLElement) => element.dataset.choice);
    }

    /**
     * The element to edit a service that has been opened after the
     * pen-icon-button (edit button) was clicked.
     * @private
     */
    private get openServiceElement(): HTMLElement {
        return document.querySelector("." + CartItem.SERVICE_TEMPLATE_MAPPING[this.activeService]);
    }

    /**
     * cart-item-service element for a service product's handle
     *
     * @param cartItemKey
     * @param serviceHandle
     * @private
     */
    private static getServiceElementsByHandle(cartItemKey: string, serviceHandle: string): HTMLElement {
        const selector = [
            `div[${CartItem.DATA_ATTRIBUTES.CART_ITEM_KEY}="${cartItemKey}"]`,
            `.service[${CartItem.DATA_ATTRIBUTES.SERVICE_HANDLE}="${serviceHandle}"]`
        ].join(" ");
        return document.querySelector(selector);
    }

    /**
     * Buttons with pen-icon that allow users to edit the service option again
     * @private
     */
    private get editButtons(): NodeListOf<HTMLElement> {
        return document.querySelectorAll(`.cart-item[data-item-key="${this.cartItemKey}"] .edit`);
    }

    /**
     * Button with trash-icon to remove item
     * @private
     */
    private get removeFromCartButton(): HTMLElement {
        return document.querySelector(
            `div[${CartItem.DATA_ATTRIBUTES.CART_ITEM_KEY}="${this.cartItemKey}"] .remove-from-cart`
        );
    }

    private get quantityInput(): HTMLElement {
        return this.cartItemElement.querySelector(`.item-product x-quantity`);
    }

    /**
     * After a service configurator was initialized, its choices need some setup
     * like removing those that are not allowed to choose, update prices according to
     * rental days, add event listeners.
     * @param configurationForm the form that includes the choices (open service element)
     * @param serviceHandle
     * @private
     */
    private initChoices(configurationForm: HTMLElement, serviceHandle: string): void {
        CartItem.addChoiceEventListeners(configurationForm);
        configurationForm.querySelectorAll<HTMLElement>(`[${CartItem.DATA_ATTRIBUTES.CHOICE}]`).forEach((element) => {
            this.removeForbiddenChoices(element);
            this.applyActiveClasses(element);
            this.initServiceQuantitySelector(element, serviceHandle);
            this.initPerDayPrices(element, serviceHandle);
            this.initPropertiesCallbacks(element, serviceHandle);
        });
        this.applyServiceChoices(configurationForm);
    }

    /**
     * call callbacks that may be defined in additional-properties dataset of choice element
     * @param choice
     * @param serviceHandle
     * @private
     */
    private initPropertiesCallbacks(choice: HTMLElement, serviceHandle: string) {
        if (choice.dataset.additionalProperties?.match(regTax)) {
            const priceElement = choice.querySelector<HTMLElement>("[data-price]");
            if (priceElement) {
                const price = this.calculatePricePerDay(priceElement, serviceHandle);
                if (price) {
                    choice.dataset.additionalProperties = choice.dataset.additionalProperties.replace(
                        regTax,
                        calculateTax(choice, price)
                    );
                }
            }
        }
    }

    /**
     * Init prices for services charged per dya
     * @param element
     * @param serviceHandle
     * @private
     */
    private initPerDayPrices(element: HTMLElement, serviceHandle: string): void {
        element.querySelectorAll<HTMLElement>("[data-price]").forEach((pricePerDayElement) => {
            const pricePerDay = this.calculatePricePerDay(pricePerDayElement, serviceHandle);
            const moneyFormat = document.querySelector<HTMLElement>("#cart-flow-header")?.dataset.moneyFormat;
            pricePerDayElement.innerHTML = formatMoney(pricePerDay, moneyFormat);
        });
    }

    /**
     * read price from element
     * @param element
     * @param serviceHandle
     * @private
     */
    private calculatePricePerDay(element: HTMLElement, serviceHandle: string): number {
        const pricePerDay = parseInt(element.dataset.price);
        const quantity = Number(
            this.cartItem.properties?.[SERVICE_PROPERTY_MAP[serviceHandle]?.service_quantity] || this.cartItem.quantity
        );
        return isNaN(pricePerDay) ? 0 : pricePerDay * quantity;
    }

    private retrieveAdditionalProperties(): { [key: string]: ICartItemProperties } {
        return additionalCartItemProperties(
            [...this.openServiceElement.querySelectorAll<HTMLElement>(`[${CartItem.DATA_ATTRIBUTES.CHOICE}]`)].filter(
                (c) => c.classList.contains(Choice.ACTIVE_CLASS)
            )
        );
    }

    /**
     * Init quantity selector for this cart item
     * @private
     */
    private initQuantityInput(): void {
        this.quantityInput?.addEventListener(EVENTS.CHANGE, async (event: CustomEvent) => {
            const updatedQuantity = Number((<Quantity>event.target).value);
            // The quantity element fires its change event a lot, even on hover
            if (this.cartItem.quantity === updatedQuantity) return;
            new LoaderOverlay().render();
            await cartManager.updateQuantity(this.cartItemKey, updatedQuantity);
            const services = (this.cartItem.properties?.[PRODUCT_SERVICES_KEY] as string[]) || [];
            const serviceItems = this.getServiceItems();
            const serviceQuantities = serviceItems.map((i) => {
                return {
                    id: String(i.id),
                    quantity: Math.min(updatedQuantity, i.quantity)
                };
            });
            const additionalCartItemProperties: { [key: string]: ICartItemProperties } = {};
            serviceItems.forEach((i) => {
                const key = String(i.id);
                additionalCartItemProperties[key] = i.properties;
                delete additionalCartItemProperties[key][SERVICE_BELONGS_TO_KEY];
            });
            await cartManager.updateServiceProperties(
                String(this.cartItemKey),
                services,
                serviceQuantities,
                additionalCartItemProperties
            );
            window.location.reload();
        });
    }

    /**
     * obtain cart items belonging to main cart item
     * @private
     */
    private getServiceItems(): ICartItem[] {
        return this.cartItems.filter((c) => {
            return c.properties?.[SERVICE_BELONGS_TO_KEY] === this.cartItem.id;
        });
    }

    /**
     * obtain service cart item by handle
     * @param handle
     * @private
     */
    private getServiceItemByHandle(handle: string): ICartItem {
        return this.getServiceItems().find((c) => {
            return c.handle === handle;
        });
    }

    /**
     * obtain service quantities
     * @param services
     * @param serviceHandle
     * @private
     */
    private getServiceQuantities(services: string[], serviceHandle: string): { id: string; quantity: number }[] {
        return services.map((id) => {
            const serviceItem = this.cartItems.find((i) => i.id === Number(id));
            // if the item is not found it is the newly added item hence we use the given handle. We use the service items handle otherwise
            const itemHandle = serviceItem?.handle || serviceHandle;
            const defaultQuantity = this.cartItem.quantity;
            const quantityByCartItemProperty = Number(
                this.cartItem.properties?.[SERVICE_PROPERTY_MAP[itemHandle]?.service_quantity]
            );
            const serviceElement = this.openServiceElement.querySelector(
                `[${CartItem.DATA_ATTRIBUTES.CHOICE}="${id}"]`
            );
            const selectedQuantity = Number(
                serviceElement?.querySelector<Quantity>(CART_SELECTORS.QUANTITY_SELECT)?.value
            );
            const quantity = quantityByCartItemProperty || selectedQuantity || defaultQuantity;
            return { id, quantity };
        });
    }
}
